/**
 * RegistrationSessionsTab - DataTable showing all registration/upgrade sessions
 * Allows support to view details and retry failed sessions
 */

import type { RegistrationSession } from '@/application/queries/registrationSessions/GetRegistrationSessionsQuery';
import { useUserPreferences } from '@/components/features/settings/UserSettings';
import {
  Badge,
  DataTable,
  DataTableColumnHeader,
} from '@/components/ui';
import { NS } from '@/i18n/namespaces';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { formatDateTimeByPrefs } from '@utils/format-locale';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegistrationSessions } from './DeveloperPlanPage.hook';
import { RetryDialog } from './RetryDialog';

// ============================================================================
// Component
// ============================================================================

export const RegistrationSessionsTab: React.FC = () => {
  const { sessions, sessionsLoading, sessionsError, refetchSessions, retryRegistration } =
    useRegistrationSessions();
  const loading = sessionsLoading;
  const error = sessionsError;
  const onRefresh = refetchSessions;
  const { t } = useTranslation(NS.DEVELOPER_PLAN);
  const { timezone } = useUserPreferences();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedSession, setSelectedSession] = useState<RegistrationSession | null>(null);

  // Format datetime for display — applies user timezone preference
  const formatDate = (dateStr: string | null) => formatDateTimeByPrefs(dateStr, timezone);

  // Status badge colors
  const statusColors: Record<string, string> = {
    pending: 'bg-blue-100 text-blue-800',
    failed: 'bg-red-100 text-red-800',
    retrying: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
  };

  // Table columns
  const columns = useMemo<ColumnDef<RegistrationSession>[]>(
    () => [
      {
        accessorKey: 'contactEmail',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('Sessions_Col_Email', 'Contact Email')}
          />
        ),
        cell: ({ row }) => (
          <a
            href={`mailto:${row.getValue('contactEmail')}`}
            className="text-primary hover:underline"
          >
            {row.getValue('contactEmail') || '—'}
          </a>
        ),
      },
      {
        accessorKey: 'companyName',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('Sessions_Col_Company', 'Company')}
          />
        ),
        cell: ({ row }) => {
          const company = row.getValue('companyName') as string | null;
          const networkId = row.original.networkId;
          if (!company && !networkId) return '—';
          return (
            <span>
              {company || '—'}
              {networkId && (
                <span className="ml-1 text-xs text-muted-foreground">({networkId})</span>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'sessionType',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('Sessions_Col_Type', 'Type')}
          />
        ),
        cell: ({ row }) => (
          <Badge variant="outline" className="capitalize">
            {row.getValue('sessionType')}
          </Badge>
        ),
      },
      {
        accessorKey: 'plan',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('Sessions_Col_Plan', 'Plan')}
          />
        ),
      },
      {
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('Sessions_Col_Status', 'Status')}
          />
        ),
        cell: ({ row }) => {
          const status = row.getValue('status') as string;
          const colorClass = statusColors[status] ?? 'bg-gray-100 text-gray-800';
          return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
              {status}
            </span>
          );
        },
      },
      {
        accessorKey: 'errorCheckpoint',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('Sessions_Col_Checkpoint', 'Checkpoint')}
          />
        ),
        cell: ({ row }) => {
          const status = row.original.status;
          if (status !== 'failed' && status !== 'retrying') return '—';
          return (
            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
              {row.getValue('errorCheckpoint') || '—'}
            </code>
          );
        },
      },
      {
        accessorKey: 'errorMessage',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('Sessions_Col_Error', 'Error')}
          />
        ),
        cell: ({ row }) => {
          const status = row.original.status;
          if (status !== 'failed' && status !== 'retrying') return '—';
          const error = row.getValue('errorMessage') as string | null;
          if (!error) return '—';

          const truncated = error.length > 40 ? `${error.slice(0, 40)}...` : error;
          return (
            <span className="text-red-600 text-sm" title={error}>
              {truncated}
            </span>
          );
        },
      },
      {
        accessorKey: 'retryCount',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('Sessions_Col_Retries', 'Retries')}
          />
        ),
        cell: ({ row }) => {
          const count = row.getValue('retryCount') as number;
          return (
            <Badge variant={count > 3 ? 'destructive' : 'secondary'}>{count}</Badge>
          );
        },
      },
      {
        accessorKey: 'updatedAt',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('Sessions_Col_UpdatedAt', 'Updated At')}
          />
        ),
        cell: ({ row }) => formatDate(row.getValue('updatedAt')),
      },
      {
        id: 'actions',
        header: () => (
          <span className="text-sm font-semibold">
            {t('Sessions_Col_Actions', 'Actions')}
          </span>
        ),
        cell: ({ row }) => {
          if (row.original.status !== 'failed') return null;
          return (
            <button
              onClick={() => setSelectedSession(row.original)}
              className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors"
            >
              {t('Sessions_Action_Retry', 'Retry')}
            </button>
          );
        },
        enableSorting: false,
      },
    ],
    [t]
  );

  // Initialize table
  const table = useReactTable({
    data: sessions,
    columns,
    state: { sorting, globalFilter },
    getRowId: (row) => row.sessionId,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = String(filterValue ?? '').toLowerCase().trim();
      if (!search) return true;
      const d = row.original;
      return (
        (d.contactEmail ?? '').toLowerCase().includes(search) ||
        (d.companyName ?? '').toLowerCase().includes(search) ||
        (d.errorMessage ?? '').toLowerCase().includes(search) ||
        (d.errorCheckpoint ?? '').toLowerCase().includes(search) ||
        (d.plan ?? '').toLowerCase().includes(search)
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Initial loading state — before any data has loaded
  if (loading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">{t('Sessions_Loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-destructive text-4xl mb-4">⚠️</div>
        <p className="text-muted-foreground">{error.message}</p>
        <button
          onClick={onRefresh}
          className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md"
        >
          {t('Sessions_Error_Retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search + Refresh */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t(
            'Sessions_Filter_Placeholder',
            'Search email, company, error...'
          )}
          className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {globalFilter && (
          <button
            onClick={() => setGlobalFilter('')}
            className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            {t('Sessions_Filter_Clear', 'Clear')}
          </button>
        )}
        <div className="ml-auto">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="px-4 py-2 text-sm border border-input bg-background rounded-md hover:bg-accent disabled:opacity-50"
          >
            {loading
              ? t('Sessions_Action_Refreshing', 'Refreshing...')
              : t('Sessions_Action_Refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        <div className="min-w-max">
          <DataTable
            table={table}
            showPagination={true}
            headerClassName="[&_th]:bg-gray-100 [&_th]:text-gray-700 dark:[&_th]:bg-gray-800 dark:[&_th]:text-gray-300"
            noResultsText={t('Sessions_NoResults', 'No sessions found.')}
            loadingText={t('Sessions_Loading', 'Loading...')}
          />
        </div>
      </div>

      {/* Retry Dialog */}
      {selectedSession && (
        <RetryDialog
          session={selectedSession}
          open={!!selectedSession}
          onOpenChange={(open) => {
            if (!open) setSelectedSession(null);
          }}
          onRetry={async (notes) => {
            await retryRegistration(
              selectedSession.sessionId,
              selectedSession.sessionType,
              notes
            );
            setSelectedSession(null);
          }}
        />
      )}
    </div>
  );
};
